import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import bookingApi from "../../../api/booking/BookingApi";
import ProcessingBar from "../../../Util/Processingbar";

const TimeSlotSelector = ({
  staffs = [],
  selectedProfessionalId,
  selectedDateTime,
  setSelectedDateTime,
}) => {
  const breakTime = 180; // ✅ lead time to book 3h = 180 minutes!!!
  const now = dayjs();
  const bufferTime = now.add(breakTime, "minute"); // ⏱️ Add break time buffer

  const [weekStart, setWeekStart] = useState(dayjs().startOf("day"));
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [userSelected, setUserSelected] = useState(false);
  const [weeklySlots, setWeeklySlots] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const dayRefs = useRef({});

  const selectedProfessional = staffs.find(
    (staff) => staff._id === selectedProfessionalId?._id
  );

  const handleDateClick = (date, dayStr) => {
    const startOfClickedWeek = date.startOf("week");

    // If selected date is outside current week, update weekStart
    if (!weekDates.some((d) => d.isSame(date, "day"))) {
      setWeekStart(startOfClickedWeek);
    }

    setSelectedDate(date);
    setUserSelected(true);
    setSelectedDateTime(null);

    const container = scrollContainerRef.current;
    const target = dayRefs.current[dayStr];

    if (container && target) {
      container.scrollTo({
        left: target.offsetLeft - container.offsetLeft,
        behavior: "smooth",
      });
    }
  };

  const weekDates = Array.from({ length: 7 }).map((_, i) =>
    weekStart.add(i, "day")
  );

  const handleSelectTime = (date, time) => {
    const fullDateTime = `${date.format("YYYY-MM-DD")} ${time}`;
    setSelectedDateTime(fullDateTime);
  };

  const fetchSlotsForDate = async (date) => {
    try {
      const { response, err } = await bookingApi.getAvailableTimeslot(
        selectedProfessionalId?._id,
        date.format("YYYY-MM-DD")
      );
      if (err || !response?.data) {
        console.error("Slot fetch error:", err);
        return [];
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching slots:", error);
      return [];
    }
  };

  const fetchWeeklySlots = async () => {
    if (!selectedProfessionalId) return;
    setIsLoading(true);
    const newSlots = {};

    await Promise.all(
      weekDates.map(async (date) => {
        const slots = await fetchSlotsForDate(date);
        newSlots[date.format("YYYY-MM-DD")] = slots;
      })
    );

    setWeeklySlots(newSlots);
    setIsLoading(false);
  };

  const availableSlots = weeklySlots[selectedDate?.format("YYYY-MM-DD")] || [];

  const goToNextWeek = () => {
    const next = weekStart.add(7, "day");
    setWeekStart(next);
    setSelectedDate(null);
    setSelectedDateTime(null);
    setUserSelected(false);
  };

  const goToPreviousWeek = () => {
    const prev = weekStart.subtract(7, "day");
    setWeekStart(prev);
    setSelectedDate(null);
    setSelectedDateTime(null);
    setUserSelected(false);
  };

  useEffect(() => {
    if (!selectedProfessionalId) return;
    fetchWeeklySlots();
  }, [weekStart, selectedProfessionalId]);

  useEffect(() => {
    const autoSelectFirstWorkingDay = async () => {
      for (let date of weekDates) {
        const formatted = date.format("YYYY-MM-DD");
        const slots = weeklySlots[formatted];

        if (!slots || slots.length === 0) continue;

        const isToday = date.isSame(now, "day");
        const hasFutureSlots = slots.some((slot) =>
          dayjs(`${formatted} ${slot.start}`, "YYYY-MM-DD hh:mm A").isAfter(
            bufferTime
          )
        );

        if (!isToday || hasFutureSlots) {
          setSelectedDate(date);
          return;
        }
      }
    };

    autoSelectFirstWorkingDay();
  }, [weeklySlots]);

  const [nextAvailableDate, setNextAvailableDate] = useState(null);

  const findNextAvailableDate = async () => {
    if (!selectedProfessionalId) return null;

    const maxSearchDays = 30; // Search next 30 days
    const startDate = dayjs().startOf("day");

    for (let i = 0; i < maxSearchDays; i++) {
      const date = startDate.add(i, "day");
      const formatted = date.format("YYYY-MM-DD");

      try {
        const { response } = await bookingApi.getAvailableTimeslot(
          selectedProfessionalId._id,
          formatted
        );

        const slots = response?.data || [];
        const hasFutureSlot = slots.some((slot) => {
          const slotDateTime = dayjs(
            `${formatted} ${slot.start}`,
            "YYYY-MM-DD hh:mm A"
          );
          return slotDateTime.isAfter(dayjs());
        });

        if (hasFutureSlot) {
          return date;
        }
      } catch (err) {
        console.error("Error checking next available:", err);
      }
    }

    return null;
  };

  useEffect(() => {
    findNextAvailableDate().then(setNextAvailableDate);
  }, [selectedProfessionalId]);

  // slow loading
  const [isSlotAnimating, setIsSlotAnimating] = useState(false);

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  useEffect(() => {
    if (!selectedDate) return;

    // Start with hidden slots
    setIsSlotAnimating(false);

    const timeout = setTimeout(() => {
      setIsSlotAnimating(true);
    }, 10); // Delay to simulate Fresha-style mount

    return () => clearTimeout(timeout);
  }, [selectedDate]);

  const selectedLabel = selectedDateTime ? (
    <>
      Booked:{" "}
      <span className="font-semibold text-yellowlight text-xl">
        {formatDateTime(selectedDateTime)}
      </span>
    </>
  ) : (
    "Select a timeslot"
  );

  return (
    <div className="p-">
      <h2 className="text-2xl font-bold mb-4">Appointment Time</h2>

      <button
        onClick={() => setIsOpen(true)}
        className={`w-full p-3 rounded-lg border text-left min-h-[2.5rem] mt-3 ${
          selectedDateTime
            ? "border-yellowlight text-white"
            : "border-border text-gray-400"
        }`}
      >
        {selectedLabel}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center"
          onClick={() => setIsOpen(false)}
        >
          {isLoading ? (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[9999]">
              <ProcessingBar />
            </div>
          ) : (
            <div
              className="bg-dry border border-yellowlight w-full max-w-2xl max-h-[90vh] rounded-xl p-4 overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-yellowlight mb-4">
                Choose a date and time
              </h2>

              <div className="p-4">
                <div className="flex items-center justify-between mb-4 text-white">
                  <span className="text-xl font-bold">
                    {weekStart.format("MMMM YYYY")}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={goToPreviousWeek}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-white/10"
                    >
                      ←
                    </button>
                    <button
                      onClick={goToNextWeek}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-white/10"
                    >
                      →
                    </button>
                  </div>
                </div>

                {/* Day Selector */}
                <div
                  ref={scrollContainerRef}
                  className="flex flex-row overflow-x-auto no-scrollbar gap-2 mb-6 scroll-smooth"
                >
                  {weekDates.map((date) => {
                    const dayStr = date.format("YYYY-MM-DD");
                    const hasSlots = weeklySlots[dayStr]?.length > 0;
                    const isSelected = selectedDate?.isSame(date, "day");

                    return (
                      <button
                        key={dayStr}
                        ref={(el) => (dayRefs.current[dayStr] = el)}
                        onClick={() => handleDateClick(date, dayStr)}
                        className="flex flex-col items-center justify-center w-16 shrink-0"
                      >
                        <div
                          className={`w-12 h-12 flex items-center justify-center rounded-full text-xl font-bold transition 
                            ${
                              isSelected
                                ? "bg-yellowlight text-yellowdark"
                                : hasSlots
                                ? "border border-gray-300 text-white hover:border-yellowlight"
                                : "border border-gray-700 text-gray-500"
                            }`}
                        >
                          {date.format("D")}
                        </div>
                        <span
                          className={`mt-1 text-lg ${
                            isSelected
                              ? "text-yellowlight font-medium"
                              : hasSlots
                              ? "text-white"
                              : "text-gray-500"
                          }`}
                        >
                          {date.format("ddd")}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="overflow-y-auto max-h-[40vh] pr-1">
                    {dayjs(selectedDate).isBefore(now, "day") ? (
                      <div className="text-center py-10 text-white">
                        <img
                          src={selectedProfessional?.photo}
                          alt={selectedProfessional?.name}
                          className="w-14 h-14 rounded-full mx-auto mb-4 object-cover"
                        />
                        <div className="text-2xl font-semibold mb-2">
                          {selectedProfessional?.name} is fully booked on this
                          date
                        </div>
                        <div className="text-sm text-gray-400 mb-6">
                          {nextAvailableDate ? (
                            <>
                              Available from{" "}
                              <span className="text-yellowlight font-medium">
                                {nextAvailableDate.format("ddd, D MMM")}
                              </span>
                            </>
                          ) : (
                            "No available slots in the next 30 days"
                          )}
                        </div>
                      </div>
                    ) : availableSlots.filter((slot) => {
                        const slotDateTime = dayjs(
                          `${selectedDate.format("YYYY-MM-DD")} ${slot.start}`,
                          "YYYY-MM-DD hh:mm A"
                        );
                        return (
                          !selectedDate.isSame(now, "day") ||
                          slotDateTime.isAfter(bufferTime)
                        );
                      }).length > 0 ? (
                      <div
                        key={selectedDate.format("YYYY-MM-DD")}
                        className="grid grid-cols-1 md:grid-cols-1 gap-3"
                      >
                        {availableSlots
                          .filter((slot) => {
                            const slotDateTime = dayjs(
                              `${selectedDate.format("YYYY-MM-DD")} ${
                                slot.start
                              }`,
                              "YYYY-MM-DD hh:mm A"
                            );
                            return (
                              !selectedDate.isSame(now, "day") ||
                              slotDateTime.isAfter(bufferTime)
                            );
                          })
                          .map((slot, index) => {
                            const fullTime = `${selectedDate.format(
                              "YYYY-MM-DD"
                            )} ${slot.start}`;
                            const isSelected = selectedDateTime === fullTime;
                            const isBooked = slot.status === "booked";
                            const delay = `${index * 100}ms`;

                            return isBooked ? (
                              <div
                                key={slot.start}
                                style={{ animationDelay: delay }}
                                className="animate-fadeInSlot px-4 py-3 text-left rounded-xl cursor-not-allowed flex justify-start items-center"
                              >
                                <span className="line-through opacity-60 italic text-xl">
                                  {slot.start}
                                </span>
                              </div>
                            ) : (
                              <button
                                key={slot.start}
                                onClick={() =>
                                  handleSelectTime(selectedDate, slot.start)
                                }
                                style={{ animationDelay: delay }}
                                className={`animate-fadeInSlot px-4 py-3 text-left rounded-xl transition text-sm font-medium flex justify-between items-center ${
                                  isSelected
                                    ? "border-yellowlight ring-2 ring-yellowlight text-yellowdark font-semibold bg-yellowlight text-xl"
                                    : "border-border text-white hover:border-yellowlight text-xl"
                                }`}
                              >
                                <span>{slot.start}</span>
                              </button>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-white">
                        <img
                          src={selectedProfessional?.photo}
                          alt={selectedProfessional?.name}
                          className="w-14 h-14 rounded-full mx-auto mb-4 object-cover"
                        />
                        <div className="text-2xl font-semibold mb-2">
                          {selectedProfessional?.name} is fully booked on this
                          date
                        </div>
                        <div className="text-sm text-gray-400 mb-6">
                          {nextAvailableDate ? (
                            <>
                              Available from{" "}
                              <span className="text-yellowlight font-medium">
                                {nextAvailableDate.format("ddd, D MMM")}
                              </span>
                            </>
                          ) : (
                            "No available slots in the next 30 days"
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setSelectedDate(null);
                      setSelectedDateTime(null);
                    }}
                    className="text-sm px-4 py-2 bg-none text-yellowlight hover:text-white rounded-md"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-sm px-4 py-2 bg-yellowlight text-yellowdark rounded-md"
                  >
                    Done
                  </button>
                </div>
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-black"
                  onClick={() => setIsOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
